import type { VisibilityLevel } from './constants/visibilityKeys';
import { VISIBILITY_LEVEL } from './constants/visibilityKeys';
import { APP_ENV } from './utils';

export type VisibilityConfigMap = Record<string, number | boolean | string | undefined> | null | undefined;

/** Default when config missing or key missing: Read (0). */
const DEFAULT_LEVEL: VisibilityLevel = VISIBILITY_LEVEL.READ;

/**
 * Normalize API/legacy value to VisibilityLevel.
 * Accepts ONLY the numbers -1|0|1 — a boolean (or anything else) returns undefined.
 */
export function normalizeLevel(value: unknown): VisibilityLevel | undefined {
    if (typeof value === 'number' && (value === -1 || value === 0 || value === 1)) {
        return value as VisibilityLevel;
    }
    return undefined;
}

/**
 * Get normalized level for a key. Always returns a level: default 0 (Read) when config missing or key missing.
 * Handles all three scenarios: -1 (Hidden), 0 (Read), 1 (Write).
 */
export function getLevel(config: VisibilityConfigMap, key: string): VisibilityLevel {
    if (!config || typeof config !== 'object') return DEFAULT_LEVEL;
    const raw = config[key];
    const level = normalizeLevel(raw);
    return level ?? DEFAULT_LEVEL;
}

/**
 * Global helper: one call gives both "show" and "disabled" for a key.
 * Use for any feature that should be hidden when -1 and disabled when 0.
 */
export type FeatureVisibility = { show: boolean; disabled: boolean; hidden: boolean; write?: boolean };

export function getFeatureVisibility(config: VisibilityConfigMap, key: string): FeatureVisibility {
    const level = getLevel(config, key);
    return {
        show: level >= VISIBILITY_LEVEL.READ,
        disabled: level === VISIBILITY_LEVEL.READ,
        hidden: level === VISIBILITY_LEVEL.HIDDEN,
        write: level === VISIBILITY_LEVEL.WRITE,
    };
}

/**
 * Fail-CLOSED enablement check, for opt-in features whose missing config must mean
 * "off" (e.g. per-PNO gated rollouts) — the opposite of getLevel's fail-open READ
 * default. Enabled only when the key is EXPLICITLY present in the config AND not
 * set to Hidden. A missing key, or a config that hasn't loaded, is disabled.
 */
export function isExplicitlyEnabled(config: VisibilityConfigMap, key: string): boolean {
    if (!config || typeof config !== 'object') return false;
    return config[key] !== undefined && getLevel(config, key) !== VISIBILITY_LEVEL.HIDDEN;
}

/**
 * Compares a string-valued config entry (e.g. a `pnoIdEcho`) against an expected value.
 * Not a level — normalizeLevel/getLevel stay numeric-only; this is a separate string
 * comparison for gates that carry a second "does this config document actually belong to
 * the caller" defence alongside isExplicitlyEnabled (a pnoIdEcho).
 */
export function echoMatches(config: VisibilityConfigMap, key: string, expected: string | null | undefined): boolean {
    if (!config || typeof config !== 'object' || !expected) return false;
    return config[key] === expected;
}

/**
 * The two-defence, fail-closed gate for a commercially-sensitive per-PNO feature:
 * isExplicitlyEnabled AND a pnoIdEcho match. Also enforces the safety-critical value
 * discipline a commercial gate needs — the enabled key's permitted values are EXACTLY the
 * numbers 1 (shown) / -1 (hidden), never true/false/0. isExplicitlyEnabled alone cannot catch
 * this: normalizeLevel falls a boolean through to DEFAULT_LEVEL=READ, so an `enabled: false`
 * config would otherwise show the feature. Never a substitute for the feature's own
 * server-side check — this is the client-side mirror only.
 */
export function isFailClosedPnoGateOpen(
    config: VisibilityConfigMap,
    enabledKey: string,
    echoKey: string,
    callerPnoId: string | null | undefined,
): boolean {
    const rawEnabled = config?.[enabledKey];
    return rawEnabled === 1 && isExplicitlyEnabled(config, enabledKey) && echoMatches(config, echoKey, callerPnoId);
}

/**
 * Effective level for the cashflow edit button.
 *
 * D27 (RECUR-28280) v1 open posture: returns WRITE unconditionally for ALL environments,
 * including production. The env/config-driven ladder below is DORMANT — preserved intact
 * as the future re-gate path. To restore grant-based access: delete the first return
 * statement and seed grants via a godzilla ViewConfig PR (closed PR #2053 is the template).
 *
 * Dormant ladder (for reference / future flip):
 * Dev/staging: always WRITE. Everything else (prod, beta, unknown, absent): fail-closed.
 * Fail-closed + config not yet loaded: HIDDEN. Key absent or explicitly -1: HIDDEN.
 * Key present and ≥ 0: getLevel's value (0=READ→disabled, 1=WRITE→enabled).
 */
export function getCashflowEditLevel(
    config: VisibilityConfigMap,
    isConfigLoaded: boolean,
    key: string,
    env: string | undefined,
): VisibilityLevel {
    return VISIBILITY_LEVEL.WRITE; // D27 (RECUR-28280): OPEN-FOR-ALL in all envs until product configures grant-based access. Delete this line (and seed grants) to restore the config-driven gate below.
    if (env === APP_ENV.DEV || env === APP_ENV.STAGING) return VISIBILITY_LEVEL.WRITE;
    if (!isConfigLoaded) return VISIBILITY_LEVEL.HIDDEN;
    if (!isExplicitlyEnabled(config, key)) return VISIBILITY_LEVEL.HIDDEN;
    return getLevel(config, key);
}
